import {
  type MentionType,
  type SocialMentionType,
} from "shared/types/mentions";

export type MediumName =
  | "Influencer Social"
  | "Magazine"
  | "Online"
  | "Podcast"
  | "Print"
  | "Radio"
  | "Social"
  | "TV";

export interface RelatedType {
  label: string;
  appLabel: string;
  field: MentionType;
  featureFlag: string;
  featureId?: number;
  checked: boolean;
  hidden?: boolean;
  color?: string;
  icon?: string;
}

export enum SocialPlatform {
  TWITTER = "twitter",
  FACEBOOK = "facebook",
  INSTAGRAM = "instagram",
  YOUTUBE = "youtube",
  REDDIT = "reddit",
  BLOG = "blog",
  FORUM = "forum",
  BLUESKY = "bluesky",
}

export enum MediumField {
  ONLINE = "online",
  PRINT = "print",
  MAGAZINE = "magazine",
  PODCAST = "podcast",
  TV = "tv",
  RADIO = "radio",
  SOCIAL = "social",
}

export interface MediumPlatform {
  label: string;
  platform: SocialPlatform;
  color: string;
  fields: SocialMentionType[];
  featureFlag: string;
  icon: string;
  checked: boolean;
  widgets?: string[];
}

export interface BaseMedium {
  medium: MediumName;
  label: string;
  field: MediumField;
  icon: string;
  color: string;
  relatedFields: string[];
  relatedLabels: string[];
  relatedLabel?: string;
  relatedTypes: RelatedType[];
  relatedCamelCaseFields: string[];
  relatedCamelCaseField?: string;
  sourceLabel: string;
  classField: string;
  resource: string;
  featureFlag: string;
  displayExcerptsFeature: string;
  triggerAlert: boolean;
  hasNotification: boolean;
  filterEnabled: boolean;
  categoryLocationFiltersEnabled: boolean;
  createActivity?: boolean;
  viewFeatureFlag?: string;
  featureAllowanceFlag?: string;
  viewWarningFeatureFlag?: string;
  viewPopupShown?: string;
  viewPopupAllowOverspend?: boolean;
  featureName?: string;
}

export interface OnlineMedium extends BaseMedium {
  field: MediumField.ONLINE;
}

export interface PrintMedium extends BaseMedium {
  field: MediumField.PRINT;
}

export interface MagazineMedium extends BaseMedium {
  field: MediumField.MAGAZINE;
}

export interface PodcastMedium extends BaseMedium {
  field: MediumField.PODCAST;
}

export interface TvMedium extends BaseMedium {
  field: MediumField.TV;
}

export interface RadioMedium extends BaseMedium {
  field: MediumField.RADIO;
}

export interface SocialMedium
  extends Omit<BaseMedium, "resource" | "classField"> {
  field: MediumField.SOCIAL;
  platforms: MediumPlatform[];
}

export interface InfluencerSocialMedium
  extends Omit<BaseMedium, "resource" | "classField"> {
  field: MediumField.SOCIAL;
}

export type ExternalSource = Omit<
  BaseMedium,
  "medium" | "field" | "displayExcerptsFeature"
>;

export type Medium =
  | OnlineMedium
  | PrintMedium
  | MagazineMedium
  | PodcastMedium
  | TvMedium
  | RadioMedium
  | SocialMedium
  | InfluencerSocialMedium;

export type MediumByClass = Exclude<
  Medium,
  InfluencerSocialMedium | SocialMedium
>;
